import { useRef, useState } from "react";
import { api } from "../api";
import { ResultSheet } from "../components/ResultSheet";
import { Centered, Header, Shell } from "../components/Shell";
import { Toast } from "../components/Toast";
import { useGame } from "../useGame";

interface View {
  question: string;
  unit: string;
  kind: "num" | "year";
  bet?: number;
  answer?: number;
  err?: string | null;
  answered: boolean;
}

const COPY = { bad_number: "Введи число" };

function fmt(n: number): string {
  return n.toLocaleString("en-US").replace(/,/g, " ");
}

export function Stavky({ date }: { date?: string | undefined }) {
  const g = useGame<View>("stavky", COPY, date);
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const state = g.screen.kind === "game" ? g.screen.state : null;
  const finished = state !== null && state.status !== "playing";

  const submit = async () => {
    const v = value.trim();
    if (!v || finished || g.busy) return;
    await g.run(() => api.guess<View>("stavky", v, date));
  };

  if (g.screen.kind === "loading") return <Shell><Centered>…</Centered></Shell>;
  if (g.screen.kind === "error") {
    return (
      <Shell>
        <Centered>
          <div>{g.screen.message}</div>
          {g.screen.retry && (
            <button type="button" onClick={() => void g.load()} className="rounded-xl bg-tgbutton px-5 py-2.5 text-sm font-semibold text-tgbuttontext">
              ще раз
            </button>
          )}
        </Centered>
      </Shell>
    );
  }
  const s = g.screen.state;
  const v = s.view;

  return (
    <Shell header={<Header left={date ? `📼 ${date}` : `#${s.number}`} title="СТАВКИ" right={date ? "" : `🔥 ${s.me.streak}`} />}>
      <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6">
        <div className="w-full max-w-[420px] rounded-2xl bg-tgsection px-5 py-6 text-center">
          <div className="text-[11px] uppercase tracking-wide text-tghint">питання дня</div>
          <div className="mt-2 text-lg font-bold leading-snug">{v.question}</div>
          <div className="mt-1 text-xs text-tghint">відповідь у: {v.unit}</div>
        </div>

        {!v.answered ? (
          <>
            <div className="text-center text-xs text-tghint">
              одна спроба, наосліп - чужі ставки побачиш після своєї.
              <br />
              найближчий до правди бере день 🏆
            </div>
            <form
              className="flex w-full max-w-[420px] gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                void submit();
              }}
            >
              <input
                ref={inputRef}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                inputMode="numeric"
                placeholder={v.kind === "year" ? "рік, напр. 1994" : "число, напр. 2 500 000"}
                autoComplete="off"
                enterKeyHint="send"
                className="h-[48px] flex-1 rounded-xl border border-[rgba(128,128,128,0.35)] bg-transparent px-4 text-center text-[18px] font-bold text-tgtext outline-none focus:border-tgbutton"
              />
              <button
                type="submit"
                disabled={g.busy || !value.trim()}
                className="h-[48px] rounded-xl bg-tgbutton px-5 text-[15px] font-bold text-tgbuttontext active:brightness-90 disabled:opacity-35"
              >
                {g.busy ? "…" : "Ставлю"}
              </button>
            </form>
          </>
        ) : (
          <div className="w-full max-w-[420px] space-y-2 text-center">
            <div className="rounded-2xl bg-tgsection px-5 py-4">
              <div className="text-xs text-tghint">твоя ставка</div>
              <div className="text-2xl font-extrabold">{v.bet !== undefined ? fmt(v.bet) : "?"}</div>
              <div className="mt-2 text-xs text-tghint">правильна відповідь</div>
              <div className="text-2xl font-extrabold text-ok">{v.answer !== undefined ? fmt(v.answer) : "?"}</div>
              {v.err && <div className="mt-2 text-sm font-semibold">{v.err === "в яблучко" ? "🎯 в яблучко!" : `промах: ${v.err}`}</div>}
            </div>
            <div className="text-xs text-tghint">хто найближче - дивись у таблиці внизу</div>
          </div>
        )}
      </div>

      <Toast message={g.toast} />
      {g.sheetOpen && finished && (
        <ResultSheet
          state={s}
          archive={Boolean(date)}
          title={v.err === "в яблучко" ? "Снайпер!" : "Ставку прийнято"}
          subtitle={`${v.err ?? ""} · стрік ${s.me.streak} 🔥`}
          scoreLabel="avg промах"
          formatScore={(n) => (n === null ? "–" : v.kind === "year" ? `±${Math.round(n / 10)} р.` : `±${(n / 10).toFixed(1)}%`)}
          onDismiss={() => g.setSheetOpen(false)}
        />
      )}
    </Shell>
  );
}
